import React, { useEffect, useState, useMemo } from 'react';
import { useClienteAuth } from '../../context/ClienteAuthContext';
import { supabase } from '../../lib/supabase';
import { Package, Scissors, Download, Eye, Calendar, User, FileText, ChevronDown, ChevronRight } from 'lucide-react';

type ExpressOrder = {
  id: string;
  name: string;
  status: string;
  delivery_date: string;
  salesperson: string;
  approved_file: string | null;
  dtf_file: string | null;
  payment_receipt: string | null;
  comment: string | null;
  created_at: string;
  updated_at: string;
};

type ExpressSample = {
  id: string;
  name: string;
  status: string;
  delivery_date: string;
  salesperson: string;
  approved_file: string | null;
  dtf_file: string | null;
  embroidery_file: string | null;
  payment_receipt: string | null;
  comment: string | null;
  created_at: string;
  updated_at: string;
};

type ExpressItem = {
  id: string;
  stock_id: string;
  quantity: number;
  product_type: string;
  size: string;
  color: string;
  created_at: string;
};

type ExpressSampleItem = {
  id: string;
  product_type: string;
  size: string;
  color: string;
  quantity: number;
  created_at: string;
};

type FilterState = 'all' | 'pending' | 'in_progress' | 'finished' | 'shipped';

const ClientePedidos: React.FC = () => {
  const { cliente, userRole } = useClienteAuth();
  const [expressOrders, setExpressOrders] = useState<ExpressOrder[]>([]);
  const [expressSamples, setExpressSamples] = useState<ExpressSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterState>('all');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [orderItems, setOrderItems] = useState<Record<string, ExpressItem[]>>({});
  const [sampleItems, setSampleItems] = useState<Record<string, ExpressSampleItem[]>>({});
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (cliente) {
      loadData();
    }
  }, [cliente]);

  const loadData = async () => {
    if (!cliente) return;
    
    try {
      setLoading(true);
      setError(null);

      // Cargar pedidos Express
      const { data: ordersData, error: ordersError } = await supabase.rpc('portal_list_express_orders', {
        p_portal_cliente_id: cliente.id
      });
      if (ordersError) throw ordersError;

      // Cargar muestras Express
      const { data: samplesData, error: samplesError } = await supabase.rpc('portal_list_express_samples', {
        p_portal_cliente_id: cliente.id
      });
      if (samplesError) throw samplesError;

      setExpressOrders(ordersData || []);
      setExpressSamples(samplesData || []);

    // Log activity
    try {
      await supabase.rpc('log_cliente_activity_simple', {
        p_cliente_id: cliente.id,
        p_action: 'Visualización de pedidos',
        p_entity_type: 'express_orders',
        p_details: `Cliente visualizó ${(ordersData || []).length} pedidos y ${(samplesData || []).length} muestras`,
        p_action_type: 'view'
      });
      console.log('Pedidos activity logged successfully');
    } catch (logError) {
      console.error('Error logging pedidos activity:', logError);
    }
    } catch (e: any) {
      console.error('Error cargando datos:', e);
      setError('No se pudieron cargar los pedidos y muestras');
    } finally {
      setLoading(false);
    }
  };

  const toggleCard = (id: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
      // Cargar items si no están cargados
      if (!orderItems[id] && !sampleItems[id]) {
        loadItems(id);
      }
    }
    setExpandedCards(newExpanded);
  };

  const loadItems = async (id: string) => {
    setLoadingItems(prev => new Set(prev).add(id));
    
    try {
      // Intentar cargar como pedido Express
      const { data: orderItemsData, error: orderItemsError } = await supabase.rpc('portal_get_express_items', {
        p_express_id: id
      });

      if (!orderItemsError && orderItemsData) {
        setOrderItems(prev => ({ ...prev, [id]: orderItemsData }));
        return;
      }

      // Si no es pedido, intentar cargar como muestra Express
      const { data: sampleItemsData, error: sampleItemsError } = await supabase.rpc('portal_get_express_sample_items', {
        p_sample_id: id
      });

      if (!sampleItemsError && sampleItemsData) {
        setSampleItems(prev => ({ ...prev, [id]: sampleItemsData }));
      }
    } catch (error) {
      console.error('Error cargando items:', error);
    } finally {
      setLoadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'finished':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'in_progress':
        return 'En Proceso';
      case 'finished':
        return 'Terminado';
      case 'shipped':
        return 'Enviado';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-AR');
  };

  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error descargando archivo:', error);
      alert('Error al descargar el archivo');
    }
  };

  // Combinar todos los items para filtrado
  const allItems = useMemo(() => {
    const orders = expressOrders.map(order => ({
      ...order,
      type: 'order' as const,
      displayName: order.name
    }));
    
    const samples = expressSamples.map(sample => ({
      ...sample,
      type: 'sample' as const,
      displayName: `Muestra: ${sample.name}`
    }));

    return [...orders, ...samples];
  }, [expressOrders, expressSamples]);

  // Filtrar items
  const filteredItems = useMemo(() => {
    if (activeFilter === 'all') return allItems;
    
    return allItems.filter(item => {
      if (item.type === 'order') {
        return item.status === activeFilter;
      } else {
        // Para muestras, mapear 'finished' a 'shipped' si es necesario
        if (activeFilter === 'finished' && item.status === 'shipped') return true;
        return item.status === activeFilter;
      }
    });
  }, [allItems, activeFilter]);

  const getFilterCount = (filter: FilterState) => {
    if (filter === 'all') return allItems.length;
    
    return allItems.filter(item => {
      if (item.type === 'order') {
        return item.status === filter;
      } else {
        if (filter === 'finished' && item.status === 'shipped') return true;
        return item.status === filter;
      }
    }).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-12 w-12 text-red-500 mb-4">
          <FileText className="h-12 w-12" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Express</h1>
            <p className="text-gray-600">Gestiona tus pedidos y muestras Express</p>
          </div>
          
          {/* Botones de Debug para Administradores */}
          {userRole === 'Administrador' && (
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  try {
                    console.log('🔍 Debug Express Items...');
                    const { data, error } = await supabase.rpc('debug_express_items_detailed');
                    if (error) throw error;
                    console.log('🔍 Express Items Debug:', data);
                    alert(`Express Items: ${data[0]?.total_records || 0} registros encontrados. Revisa la consola para detalles.`);
                  } catch (e: any) {
                    console.error('Error en debug:', e);
                    alert(`Error en debug: ${e.message}`);
                  }
                }}
                className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
              >
                🔍 Debug Items
              </button>
              
              <button
                onClick={async () => {
                  try {
                    console.log('🔍 Debug Express Samples...');
                    const { data, error } = await supabase.rpc('debug_express_sample_items_detailed');
                    if (error) throw error;
                    console.log('🔍 Express Samples Debug:', data);
                    alert(`Express Samples: ${data[0]?.total_records || 0} registros encontrados. Revisa la consola para detalles.`);
                  } catch (e: any) {
                    console.error('Error en debug:', e);
                    alert(`Error en debug: ${e.message}`);
                  }
                }}
                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
              >
                🔍 Debug Samples
              </button>
              
              <button
                onClick={async () => {
                  try {
                    console.log('🔍 Debug Client Data...');
                    const { data, error } = await supabase.rpc('debug_client_express_data');
                    if (error) throw error;
                    console.log('🔍 Client Express Data Debug:', data);
                    alert(`Client Data: ${data.length} clientes con datos. Revisa la consola para detalles.`);
                  } catch (e: any) {
                    console.error('Error en debug:', e);
                    alert(`Error en debug: ${e.message}`);
                  }
                }}
                className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
              >
                🔍 Debug Clientes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors shadow-sm ${
              activeFilter === 'all'
                ? 'bg-black text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Todos ({allItems.length})
          </button>
          <button
            onClick={() => setActiveFilter('pending')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors shadow-sm ${
              activeFilter === 'pending'
                ? 'bg-black text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pendientes ({getFilterCount('pending')})
          </button>
          <button
            onClick={() => setActiveFilter('in_progress')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors shadow-sm ${
              activeFilter === 'in_progress'
                ? 'bg-black text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            En proceso ({getFilterCount('in_progress')})
          </button>
          <button
            onClick={() => setActiveFilter('finished')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors shadow-sm ${
              activeFilter === 'finished'
                ? 'bg-black text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Terminados ({getFilterCount('finished')})
          </button>
          <button
            onClick={() => setActiveFilter('shipped')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors shadow-sm ${
              activeFilter === 'shipped'
                ? 'bg-black text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Enviados ({getFilterCount('shipped')})
          </button>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div className="text-center py-16">
          <div className="mx-auto h-16 w-16 text-gray-300">
            <Package className="h-16 w-16" />
          </div>
          <h3 className="mt-4 text-base sm:text-lg font-semibold text-gray-900">No hay pedidos o muestras</h3>
          <p className="mt-1 text-sm text-gray-500">
            {activeFilter === 'all' 
              ? 'No tienes pedidos o muestras Express.'
              : `No hay items en estado "${activeFilter === 'pending' ? 'Pendiente' : activeFilter === 'in_progress' ? 'En Proceso' : activeFilter === 'finished' ? 'Terminado' : 'Enviado'}"`}
          </p>
        </div>
      ) : (
        <div className="grid gap-6">
          {filteredItems.map((item) => (
            <div key={`${item.type}-${item.id}`} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
              {/* Header del item - Siempre visible */}
              <div 
                className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white cursor-pointer"
                onClick={() => toggleCard(item.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        {item.type === 'order' ? (
                          <Package className="h-5 w-5 text-blue-600" />
                        ) : (
                          <Scissors className="h-5 w-5 text-purple-600" />
                        )}
                        <h3 className="text-xl font-semibold text-gray-900">{item.displayName}</h3>
                      </div>
                      <svg 
                        className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                          expandedCards.has(item.id) ? 'rotate-180' : ''
                        }`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    <div className="flex items-center space-x-4 text-sm mt-2">
                      <span className="text-gray-500">Creado: {formatDateTime(item.created_at)}</span>
                      {item.delivery_date && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="text-gray-500">
                            Entrega: <span className="font-medium text-gray-700">{formatDate(item.delivery_date)}</span>
                          </span>
                        </>
                      )}
                      <span className="text-gray-300">•</span>
                      <span className="text-gray-500">
                        Vendedor: <span className="font-medium text-gray-700">{item.salesperson}</span>
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(item.status)}`}>
                      {getStatusDisplay(item.status)}
                    </span>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                      item.type === 'order' 
                        ? 'bg-gray-100 text-gray-800 border-gray-300'
                        : 'bg-gray-100 text-gray-800 border-gray-300'
                    }`}>
                      {item.type === 'order' ? 'Pedido' : 'Muestra'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contenido expandible */}
              {expandedCards.has(item.id) && (
                <>
                  {/* Archivos */}
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Archivos</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {item.approved_file && (
                        <button
                          onClick={() => downloadFile(item.approved_file!, `ficha_aprobada_${item.name}.pdf`)}
                          className="flex items-center justify-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-200 rounded-lg hover:bg-green-200 transition-colors"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Ficha Aprobada
                        </button>
                      )}
                      {item.dtf_file && (
                        <button
                          onClick={() => downloadFile(item.dtf_file!, `dtf_${item.name}.pdf`)}
                          className="flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-200 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          DTF
                        </button>
                      )}
                      {item.type === 'sample' && item.embroidery_file && (
                        <button
                          onClick={() => downloadFile(item.embroidery_file!, `bordado_${item.name}.pdf`)}
                          className="flex items-center justify-center px-3 py-2 text-sm font-medium text-purple-700 bg-purple-100 border border-purple-200 rounded-lg hover:bg-purple-200 transition-colors"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Bordado
                        </button>
                      )}
                      {item.payment_receipt && (
                        <button
                          onClick={() => downloadFile(item.payment_receipt!, `comprobante_${item.name}.pdf`)}
                          className="flex items-center justify-center px-3 py-2 text-sm font-medium text-orange-700 bg-orange-100 border border-orange-200 rounded-lg hover:bg-orange-200 transition-colors"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Comprobante
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Items del pedido/muestra */}
                  <div className="px-4 sm:px-6 py-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Items</h4>
                    {loadingItems.has(item.id) ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {item.type === 'order' && orderItems[item.id] ? (
                          orderItems[item.id].map((orderItem) => (
                            <div key={orderItem.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                              <div className="flex items-center space-x-4">
                                <span className="text-sm font-medium text-gray-900">{orderItem.product_type}</span>
                                <span className="text-sm text-gray-600">Talle: {orderItem.size}</span>
                                <span className="text-sm text-gray-600">Color: {orderItem.color}</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">Cantidad: {orderItem.quantity}</span>
                            </div>
                          ))
                        ) : item.type === 'sample' && sampleItems[item.id] ? (
                          sampleItems[item.id].map((sampleItem) => (
                            <div key={sampleItem.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                              <div className="flex items-center space-x-4">
                                <span className="text-sm font-medium text-gray-900">{sampleItem.product_type}</span>
                                <span className="text-sm text-gray-600">Talle: {sampleItem.size}</span>
                                <span className="text-sm text-gray-600">Color: {sampleItem.color}</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">Cantidad: {sampleItem.quantity}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-4">No hay items para mostrar</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Comentarios */}
                  {item.comment && (
                    <div className="px-6 py-4 border-t border-gray-100">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Comentarios</h4>
                      <p className="text-sm text-gray-600">{item.comment}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Resumen de resultados */}
      {filteredItems.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-500">
          Mostrando {filteredItems.length} de {allItems.length} items
          {activeFilter !== 'all' && ` en estado "${activeFilter === 'pending' ? 'Pendiente' : activeFilter === 'in_progress' ? 'En Proceso' : activeFilter === 'finished' ? 'Terminado' : 'Enviado'}"`}
        </div>
      )}
    </div>
  );
};

export default ClientePedidos;
